import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BarChart3,
  Users,
  MessageSquare,
  TrendingUp,
  Calendar,
  AlertTriangle,
  Hash,
  Activity,
  Clock,
  Target,
  Zap,
  Award
} from 'lucide-react';

const AnalyticsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState([]);
  const [channelStats, setChannelStats] = useState([]);
  const [topContributors, setTopContributors] = useState([]);
  const [retention, setRetention] = useState(null);
  const [contentQuality, setContentQuality] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllAnalytics();
  }, []);

  const fetchAllAnalytics = async () => {
    try {
      const [
        overviewRes,
        activityRes,
        channelsRes,
        contributorsRes,
        retentionRes,
        qualityRes,
        heatmapRes
      ] = await Promise.all([
        api.get('/analytics/overview'),
        api.get('/analytics/activity?days=14'),
        api.get('/analytics/channels'),
        api.get('/analytics/top-contributors'),
        api.get('/analytics/retention'),
        api.get('/analytics/content-quality'),
        api.get('/analytics/engagement-heatmap')
      ]);
      
      setOverview(overviewRes.data);
      setActivity(activityRes.data || []);
      setChannelStats(channelsRes.data || []);
      setTopContributors(contributorsRes.data || []);
      setRetention(retentionRes.data);
      setContentQuality(qualityRes.data);
      setHeatmap(heatmapRes.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur de chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-green-400';
      case 'good': return 'text-yellow-400';
      default: return 'text-red-400';
    }
  };

  const getHeatmapColor = (value, max) => {
    if (!max || value === 0) return 'bg-secondary/30';
    const intensity = value / max;
    if (intensity > 0.7) return 'bg-primary';
    if (intensity > 0.4) return 'bg-primary/60';
    if (intensity > 0.2) return 'bg-primary/30';
    return 'bg-primary/10';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <BarChart3 className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  const maxHeatmapValue = heatmap ? Math.max(...heatmap.heatmap.flat()) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">Analytics</h1>
              <p className="text-sm text-muted-foreground">Tableau de bord de la communauté</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Membres actifs</p>
                  <p className="text-3xl font-bold">{overview?.users?.active || 0}</p>
                </div>
                <Users className="w-8 h-8 text-primary opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">sur {overview?.users?.total || 0} total</p>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Messages (7j)</p>
                  <p className="text-3xl font-bold">{overview?.messages?.last_7_days || 0}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-primary opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">~{overview?.messages?.daily_average || 0}/jour</p>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Taux d'engagement</p>
                  <p className="text-3xl font-bold">{overview?.engagement?.rate || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{overview?.engagement?.active_posters_30d || 0} contributeurs</p>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Signalements</p>
                  <p className="text-3xl font-bold">{overview?.moderation?.pending_reports || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-400 opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">en attente</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="engagement" className="space-y-6">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="engagement">Engagement</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="contributors">Contributeurs</TabsTrigger>
            <TabsTrigger value="quality">Qualité</TabsTrigger>
          </TabsList>

          {/* Engagement Tab */}
          <TabsContent value="engagement" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Retention Metrics */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Rétention & Santé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <span>Santé communauté</span>
                    <Badge className={getHealthColor(retention?.health_status)}>
                      {retention?.health_status === 'excellent' ? '🌟 Excellent' : 
                       retention?.health_status === 'good' ? '✅ Bon' : '⚠️ À surveiller'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-secondary/20 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{retention?.dau || 0}</p>
                      <p className="text-xs text-muted-foreground">DAU</p>
                    </div>
                    <div className="text-center p-3 bg-secondary/20 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{retention?.wau || 0}</p>
                      <p className="text-xs text-muted-foreground">WAU</p>
                    </div>
                    <div className="text-center p-3 bg-secondary/20 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{retention?.mau || 0}</p>
                      <p className="text-xs text-muted-foreground">MAU</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Taux d'activation</span>
                      <span>{retention?.activation_rate || 0}%</span>
                    </div>
                    <Progress value={retention?.activation_rate || 0} className="h-2" />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Stickiness (DAU/MAU)</span>
                      <span>{retention?.stickiness || 0}%</span>
                    </div>
                    <Progress value={retention?.stickiness || 0} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Activity Heatmap */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Activité par heure
                  </CardTitle>
                  <CardDescription>Moments les plus actifs de la semaine</CardDescription>
                </CardHeader>
                <CardContent>
                  {heatmap && (
                    <div className="space-y-1">
                      <div className="flex gap-1 text-xs text-muted-foreground mb-2">
                        <div className="w-16" />
                        {[0, 6, 12, 18, 23].map(h => (
                          <div key={h} className="flex-1 text-center">{h}h</div>
                        ))}
                      </div>
                      {heatmap.heatmap.map((dayData, dayIndex) => (
                        <div key={dayIndex} className="flex gap-1 items-center">
                          <div className="w-16 text-xs text-muted-foreground">{heatmap.days[dayIndex].slice(0, 3)}</div>
                          {dayData.map((value, hourIndex) => (
                            <div
                              key={hourIndex}
                              className={`flex-1 h-4 rounded ${getHeatmapColor(value, maxHeatmapValue)}`}
                              title={`${heatmap.days[dayIndex]} ${hourIndex}h: ${value} messages`}
                            />
                          ))}
                        </div>
                      ))}
                      <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                        <span>Moins</span>
                        <div className="flex gap-1">
                          <div className="w-4 h-4 rounded bg-primary/10" />
                          <div className="w-4 h-4 rounded bg-primary/30" />
                          <div className="w-4 h-4 rounded bg-primary/60" />
                          <div className="w-4 h-4 rounded bg-primary" />
                        </div>
                        <span>Plus</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Activity Graph */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Activité des 14 derniers jours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40">
                  {activity.map((day, index) => {
                    const maxMessages = Math.max(...activity.map(d => d.messages));
                    const height = maxMessages > 0 ? (day.messages / maxMessages) * 100 : 0;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className="w-full bg-primary rounded-t transition-all hover:bg-primary/80"
                          style={{ height: `${height}%`, minHeight: day.messages > 0 ? '4px' : '0' }}
                          title={`${day.date}: ${day.messages} messages`}
                        />
                        <span className="text-xs text-muted-foreground rotate-45 origin-left">
                          {new Date(day.date).getDate()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Channels Tab */}
          <TabsContent value="channels">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Hash className="w-5 h-5 text-primary" />
                  Performance des channels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {channelStats.map((channel, index) => (
                      <div key={channel.id} className="p-4 bg-secondary/20 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-medium">#{channel.name}</span>
                            <Badge variant="outline">{channel.type}</Badge>
                          </div>
                          <Badge>{index + 1}</Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Messages total</p>
                            <p className="text-xl font-bold">{channel.total_messages}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">7 derniers jours</p>
                            <p className="text-xl font-bold text-primary">{channel.messages_last_7_days}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Contributeurs</p>
                            <p className="text-xl font-bold">{channel.unique_contributors}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Abonnés</p>
                            <p className="text-xl font-bold">{channel.subscribers}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contributors Tab */}
          <TabsContent value="contributors" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Top par messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(Array.isArray(topContributors) ? topContributors : []).map((user, index) => (
                      <div key={user.id} className="flex items-center gap-3 p-2 bg-secondary/20 rounded-lg">
                        <span className="w-6 text-center font-bold text-muted-foreground">{index + 1}</span>
                        <div className="flex-1">
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.messages || 0} messages</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{user.messages || 0}</p>
                          <p className="text-xs text-muted-foreground">messages</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Top par points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(Array.isArray(topContributors) ? topContributors : []).map((user, index) => (
                      <div key={user.id} className="flex items-center gap-3 p-2 bg-secondary/20 rounded-lg">
                        <span className="w-6 text-center font-bold text-muted-foreground">{index + 1}</span>
                        <div className="flex-1">
                          <p className="font-medium">{user.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{user.messages || 0}</p>
                          <p className="text-xs text-muted-foreground">messages</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Users by Role */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Répartition par rôle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  {overview?.users?.by_role && Object.entries(overview.users.by_role).map(([role, count]) => (
                    <div key={role} className="text-center p-4 bg-secondary/20 rounded-lg">
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground capitalize">{role.replace('_', ' ')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quality Tab */}
          <TabsContent value="quality">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    Score de qualité
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-6xl font-bold text-primary">{contentQuality?.quality_score || 0}</p>
                    <p className="text-muted-foreground">/ 100</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Longueur moyenne des messages</span>
                        <span>{contentQuality?.average_message_length || 0} caractères</span>
                      </div>
                      <Progress value={Math.min(100, (contentQuality?.average_message_length || 0) / 2)} className="h-2" />
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Taux de réaction</span>
                        <span>{contentQuality?.reaction_rate || 0}%</span>
                      </div>
                      <Progress value={contentQuality?.reaction_rate || 0} className="h-2" />
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Taux de signalement</span>
                        <span>{contentQuality?.report_rate || 0}%</span>
                      </div>
                      <Progress value={contentQuality?.report_rate || 0} className="h-2 bg-red-500/20" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading">Types de contenu</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                      <span>Messages texte</span>
                      <span className="font-bold">{contentQuality?.messages_by_type?.text || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                      <span>Vidéos</span>
                      <span className="font-bold">{contentQuality?.messages_by_type?.video || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                      <span>Images</span>
                      <span className="font-bold">{contentQuality?.messages_by_type?.image || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/30">
                      <span>Messages épinglés</span>
                      <span className="font-bold text-primary">{contentQuality?.pinned_messages || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalyticsPage;
